<script setup>
import { ref, watch, computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import MultiSelect from 'primevue/multiselect';
import { BUSINESS_MODELS } from '@/constants/permissions.js';

const props = defineProps({
  basePermissions: {
    type: Array,
    default: () => []
  },
  extraPermissions: {
    type: Array,
    default: () => []
  },
  revokedPermissions: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:extra-permissions', 'update:revoked-permissions']);

// Локальные копии для работы
const localExtra = ref([]);
const localRevoked = ref([]);

watch(() => [props.extraPermissions, props.revokedPermissions], ([extra, revoked]) => {
  localExtra.value = [...(extra || [])];
  localRevoked.value = [...(revoked || [])];
}, { immediate: true });

// Проверка типа права
function getPermissionType(permCode) {
  const isBase = props.basePermissions.includes(permCode);
  const isExtra = localExtra.value.includes(permCode);
  const isRevoked = localRevoked.value.includes(permCode);

  if (isBase && isRevoked) return 'revoked'; // Отозванное базовое
  if (isBase) return 'base'; // Базовое активное
  if (isExtra) return 'extra'; // Дополнительное
  return 'none'; // Нет права
}

// Проверка активности права (чекбокс отмечен)
function isPermissionActive(permCode) {
  const type = getPermissionType(permCode);
  return type === 'base' || type === 'extra';
}

// Переключение права
function togglePermission(permCode) {
  const isBase = props.basePermissions.includes(permCode);
  const isExtra = localExtra.value.includes(permCode);
  const isRevoked = localRevoked.value.includes(permCode);

  if (isBase && !isRevoked) {
    // Базовое право — отозвать
    localRevoked.value.push(permCode);
  } else if (isBase && isRevoked) {
    // Отозванное базовое — вернуть
    localRevoked.value = localRevoked.value.filter(p => p !== permCode);
  } else if (isExtra) {
    // Дополнительное — удалить
    localExtra.value = localExtra.value.filter(p => p !== permCode);
  } else {
    // Нет права — добавить как дополнительное
    localExtra.value.push(permCode);
  }

  emitUpdate();
}

// Обработка специальных прав (MultiSelect)
function updateSpecialPermissions(modelId, selectedCodes, allSpecialOptions) {
  // Получаем текущие специальные права этой модели
  const currentSpecialPerms = allSpecialOptions.map(opt => opt.code);

  // Разбираем каждое специальное право
  currentSpecialPerms.forEach(permCode => {
    const shouldBeActive = selectedCodes.includes(permCode);
    const isCurrentlyActive = isPermissionActive(permCode);

    if (shouldBeActive && !isCurrentlyActive) {
      // Нужно активировать
      const type = getPermissionType(permCode);
      if (type === 'revoked') {
        // Вернуть отозванное базовое право
        localRevoked.value = localRevoked.value.filter(p => p !== permCode);
      } else {
        // Добавить как дополнительное
        localExtra.value.push(permCode);
      }
    } else if (!shouldBeActive && isCurrentlyActive) {
      // Нужно деактивировать
      const type = getPermissionType(permCode);
      if (type === 'base') {
        // Отозвать базовое право
        localRevoked.value.push(permCode);
      } else if (type === 'extra') {
        // Удалить дополнительное
        localExtra.value = localExtra.value.filter(p => p !== permCode);
      }
    }
  });

  emitUpdate();
}

function emitUpdate() {
  emit('update:extra-permissions', [...localExtra.value]);
  emit('update:revoked-permissions', [...localRevoked.value]);
}

// Данные для таблицы
const tableData = computed(() => {
  return BUSINESS_MODELS.map(model => {
    const viewPerm = model.permissions.view;
    const addPerm = model.permissions.add;
    const changePerm = model.permissions.change;
    const deletePerm = model.permissions.delete;

    // Для специальных прав собираем только активные
    const activeSpecial = model.permissions.special
      .map(sp => sp.code)
      .filter(code => isPermissionActive(code));

    // Для специальных прав с типами
    const specialWithTypes = model.permissions.special.map(sp => ({
      ...sp,
      type: getPermissionType(sp.code),
      active: isPermissionActive(sp.code)
    }));

    return {
      id: model.id,
      label: model.label,
      view: {
        code: viewPerm,
        active: isPermissionActive(viewPerm),
        type: getPermissionType(viewPerm)
      },
      add: {
        code: addPerm,
        active: isPermissionActive(addPerm),
        type: getPermissionType(addPerm)
      },
      change: {
        code: changePerm,
        active: isPermissionActive(changePerm),
        type: getPermissionType(changePerm)
      },
      delete: {
        code: deletePerm,
        active: isPermissionActive(deletePerm),
        type: getPermissionType(deletePerm)
      },
      special: activeSpecial,
      specialOptions: model.permissions.special,
      specialWithTypes: specialWithTypes
    };
  });
});
</script>

<template>
  <div class="user-permissions-matrix">
    <DataTable
      :value="tableData"
      scrollable
      scroll-height="500px"
      class="text-sm"
    >
      <Column field="label" header="Модель" frozen style="min-width: 150px; font-weight: 600;"></Column>

      <Column header="Просмотр" style="width: 100px; text-align: center;">
        <template #body="{ data }">
          <Checkbox
            :model-value="data.view.active"
            :binary="true"
            :class="`checkbox-${data.view.type}`"
            @update:model-value="togglePermission(data.view.code)"
          />
        </template>
      </Column>

      <Column header="Добавление" style="width: 110px; text-align: center;">
        <template #body="{ data }">
          <Checkbox
            :model-value="data.add.active"
            :binary="true"
            :class="`checkbox-${data.add.type}`"
            @update:model-value="togglePermission(data.add.code)"
          />
        </template>
      </Column>

      <Column header="Изменение" style="width: 110px; text-align: center;">
        <template #body="{ data }">
          <Checkbox
            :model-value="data.change.active"
            :binary="true"
            :class="`checkbox-${data.change.type}`"
            @update:model-value="togglePermission(data.change.code)"
          />
        </template>
      </Column>

      <Column header="Удаление" style="width: 100px; text-align: center;">
        <template #body="{ data }">
          <Checkbox
            :model-value="data.delete.active"
            :binary="true"
            :class="`checkbox-${data.delete.type}`"
            @update:model-value="togglePermission(data.delete.code)"
          />
        </template>
      </Column>

      <Column header="Специальные действия" style="min-width: 300px;">
        <template #body="{ data }">
          <div v-if="data.specialOptions.length > 0" class="flex flex-wrap gap-2">
            <div
              v-for="specialPerm in data.specialWithTypes"
              :key="specialPerm.code"
              class="flex items-center gap-1"
            >
              <Checkbox
                :model-value="specialPerm.active"
                :binary="true"
                :class="`checkbox-${specialPerm.type}`"
                @update:model-value="togglePermission(specialPerm.code)"
              />
              <label class="text-xs cursor-pointer" @click="togglePermission(specialPerm.code)">
                {{ specialPerm.label }}
              </label>
            </div>
          </div>
          <span v-else class="text-gray-400">—</span>
        </template>
      </Column>
    </DataTable>

    <!-- Легенда -->
    <div class="mt-4 flex gap-4 text-xs">
      <div class="flex items-center gap-2">
        <span class="legend-checkbox checkbox-base"></span>
        <span>Базовое право (от отдела/должности)</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="legend-checkbox checkbox-extra"></span>
        <span>Дополнительное право</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="legend-checkbox checkbox-revoked"></span>
        <span>Отозванное право</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.user-permissions-matrix :deep(.p-datatable-thead > tr > th) {
  font-size: 0.875rem;
  padding: 0.5rem;
}

.user-permissions-matrix :deep(.p-datatable-tbody > tr > td) {
  padding: 0.5rem;
}

/* Базовое право (серый чекбокс) */
.checkbox-base :deep(.p-checkbox-box) {
  background: #d0d0d0 !important;
  border-color: #9e9e9e !important;
}

.checkbox-base :deep(.p-checkbox-icon) {
  color: #666 !important;
}

/* Дополнительное право (зеленый чекбокс) */
.checkbox-extra :deep(.p-checkbox-box) {
  background: #4caf50 !important;
  border-color: #4caf50 !important;
}

.checkbox-extra :deep(.p-checkbox-icon) {
  color: white !important;
}

/* Отозванное право (красный чекбокс) */
.checkbox-revoked :deep(.p-checkbox-box) {
  background-color: transparent !important;
  border-color: #f44336 !important;
  border-width: 1px !important;
}

/* Легенда */
.legend-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  display: inline-block;
  border: 1px solid;
}

.legend-checkbox.checkbox-base {
  background-color: #9e9e9e;
  border-color: #9e9e9e;
}

.legend-checkbox.checkbox-extra {
  background-color: #4caf50;
  border-color: #4caf50;
}

.legend-checkbox.checkbox-revoked {
  background-color: transparent;
  border-color: #f44336;
}
</style>
